/**
 * The shared anchor every pay-period fixture in this pilot converts
 * through: Pay Period 19 starts here, and every other period's own start
 * is a whole multiple of `PERIOD_LENGTH_DAYS` away from it, in either
 * direction. This is what lets "which period is current" roll forward
 * with the real calendar instead of staying frozen to a fixed 2026
 * window: nothing below is specific to 19, 20, or any other single
 * period number, so the same conversion keeps working indefinitely as
 * real time keeps advancing past whatever period happens to be current
 * when this runs.
 */
const ANCHOR_PERIOD_NUMBER = 19
const ANCHOR_PERIOD_START = '2026-09-08'
export const PERIOD_LENGTH_DAYS = 14

const MS_PER_DAY = 24 * 60 * 60 * 1000
const WEEKDAY_ABBREVIATIONS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

// Date.UTC rather than `new Date(dateISO)`, so a caller in any timezone
// gets the same calendar day back: parsing a bare "YYYY-MM-DD" string as
// local time can land on the wrong side of midnight west of UTC, the same
// reasoning staffingTargetStore.ts's own weekdayNameFor already follows.
function utcDate(dateISO: string): Date {
  const [year, month, day] = dateISO.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

function isoDateFor(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
}

// The first day of the given period number, as an ISO date.
export function periodStartDate(periodNumber: number): string {
  const anchor = utcDate(ANCHOR_PERIOD_START)
  anchor.setUTCDate(anchor.getUTCDate() + (periodNumber - ANCHOR_PERIOD_NUMBER) * PERIOD_LENGTH_DAYS)
  return isoDateFor(anchor)
}

// The inverse of periodStartDate: which period number a given date falls
// in, however far from the anchor it is.
export function periodNumberFor(dateISO: string): number {
  const daysFromAnchor = Math.floor((utcDate(dateISO).getTime() - utcDate(ANCHOR_PERIOD_START).getTime()) / MS_PER_DAY)
  return ANCHOR_PERIOD_NUMBER + Math.floor(daysFromAnchor / PERIOD_LENGTH_DAYS)
}

// "Sep 8–21" style, from the period's own start plus PERIOD_LENGTH_DAYS-1.
export function periodRangeLabel(periodNumber: number): string {
  const start = utcDate(periodStartDate(periodNumber))
  const end = new Date(start.getTime())
  end.setUTCDate(end.getUTCDate() + PERIOD_LENGTH_DAYS - 1)
  const startLabel = `${MONTH_NAMES[start.getUTCMonth()].slice(0, 3)} ${start.getUTCDate()}`
  const endLabel =
    start.getUTCMonth() === end.getUTCMonth()
      ? `${end.getUTCDate()}`
      : `${MONTH_NAMES[end.getUTCMonth()].slice(0, 3)} ${end.getUTCDate()}`
  return `${startLabel}–${endLabel}`
}

export type PeriodDateSkeleton = { date: string; weekday: string; day: number; month: string }

// "Sep 16" for one day, "Sep 22–23" for several in the same month, built
// from the fixture's own date entries rather than typed out separately,
// so a label can never disagree with the dates it names.
export function dateRangeLabel(days: { month: string; day: number }[]): string {
  const first = days[0]
  const last = days[days.length - 1]
  const firstLabel = `${first.month.slice(0, 3)} ${first.day}`
  if (days.length === 1) return firstLabel
  return first.month === last.month ? `${firstLabel}–${last.day}` : `${firstLabel}–${last.month.slice(0, 3)} ${last.day}`
}

// The bare 14-day calendar skeleton for a period, in order from its own
// first day: every other fixture in this pilot (a full DayEntry array, a
// weekday-keyed schedule default, an assignment rotation) is built by
// filling this in, never by hand-authoring calendar dates directly.
export function periodDates(periodNumber: number): PeriodDateSkeleton[] {
  const start = utcDate(periodStartDate(periodNumber))
  return Array.from({ length: PERIOD_LENGTH_DAYS }, (_, offset) => {
    const date = new Date(start.getTime())
    date.setUTCDate(date.getUTCDate() + offset)
    return {
      date: isoDateFor(date),
      weekday: WEEKDAY_ABBREVIATIONS[date.getUTCDay()],
      day: date.getUTCDate(),
      month: MONTH_NAMES[date.getUTCMonth()],
    }
  })
}
